import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { Button } from "../Button";
import styled from 'styled-components';
import { theme } from "../../theme";
import { PopoverContext } from "../Popover";
import { useForkedRef } from "../../hooks";
import { useRegisterMenuItem } from "./useRegisterMenuItem";
/**
 * MenuLink / MenuPrimaryAction
 *
 * This has its own unique appearance
 * and behavior, while still retaining MenuItem's selection
 * and keyboard nav states.
 */

export var MenuLink = React.forwardRef(function (_ref, forwardedRef) {
  var _ref$shouldCloseMenuO = _ref.shouldCloseMenuOnSelect,
      shouldCloseMenuOnSelect = _ref$shouldCloseMenuO === void 0 ? true : _ref$shouldCloseMenuO,
      children = _ref.children,
      _ref$color = _ref.color,
      color = _ref$color === void 0 ? 'secondary' : _ref$color,
      _ref$as = _ref.as,
      as = _ref$as === void 0 ? 'button' : _ref$as,
      onSelect = _ref.onSelect,
      onClickProp = _ref.onClick,
      disabled = _ref.disabled,
      iconAfter = _ref.iconAfter,
      href = _ref.href,
      other = _objectWithoutProperties(_ref, ["shouldCloseMenuOnSelect", "children", "color", "as", "onSelect", "onClick", "disabled", "iconAfter", "href"]);

  var ownRef = useRef(null);
  var ref = useForkedRef(forwardedRef, ownRef);

  var _useContext = useContext(PopoverContext),
      onRequestClose = _useContext.onRequestClose;

  var _useRegisterMenuItem = useRegisterMenuItem(ownRef),
      isSelected = _useRegisterMenuItem.isSelected;

  var buttonRef = useRef(null);
  var renderIconAfter = useCallback(function () {
    if (!iconAfter) {
      return null;
    }

    return /*#__PURE__*/React.createElement(IconAfterWrapper, null, iconAfter);
  }, [iconAfter]);
  useEffect(function () {
    if (isSelected) {
      buttonRef.current.focus();
    }
  }, [isSelected]);

  function handleClick(event) {
    if (shouldCloseMenuOnSelect) {
      onRequestClose();
    }

    if (onClickProp) {
      onClickProp(event);
    }

    if (onSelect) {
      onSelect();
    }
  }

  return /*#__PURE__*/React.createElement(Container, {
    ref: ref
  }, /*#__PURE__*/React.createElement(MockClickableArea, null, /*#__PURE__*/React.createElement(StyledButton, _extends({
    block: true,
    forwardedAs: as,
    color: color,
    role: "menuitem",
    disabled: disabled,
    ref: buttonRef,
    onClick: handleClick,
    $isSelected: isSelected,
    $iconAfter: iconAfter,
    href: href
  }, other), children, renderIconAfter())));
}); // We are now encouraging the use of `MenuPrimaryAction`
// since this more accurately reflects latest designs.
// For now, let's alias the name to MenuLink.

export var MenuPrimaryAction = MenuLink;
var Container = styled.div.withConfig({
  displayName: "MenuLink__Container",
  componentId: "vapor__synbw4-0"
})(["padding:0 ", ";"], theme.spaces[8]);
var MockClickableArea = styled.div.withConfig({
  displayName: "MenuLink__MockClickableArea",
  componentId: "vapor__synbw4-1"
})(["cursor:pointer;padding:", " 0;"], theme.spaces[2]);
var StyledButton = styled(Button).withConfig({
  displayName: "MenuLink__StyledButton",
  componentId: "vapor__synbw4-2"
})(["display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;", ""], function (p) {
  return p.$isSelected && "\n     box-shadow: ".concat(theme.shadows.interactive.focus, ";\n  ");
});
var IconAfterWrapper = styled.div.withConfig({
  displayName: "MenuLink__IconAfterWrapper",
  componentId: "vapor__synbw4-3"
})(["position:absolute;right:", ";top:0;bottom:0;display:flex;align-items:center;"], theme.spaces[4]);